import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, TrendingUp, Calendar } from 'lucide-react';
interface ReportHeroProps {
  month: string;
  year: number;
  score: number;
  previousScore?: number;
}
export function ReportHero({
  month,
  year,
  score,
  previousScore
}: ReportHeroProps) {
  // Determine score category with refined colors
  const getScoreCategory = (s: number) => {
    if (s <= 30) return {
      label: 'Healthy',
      color: 'text-emerald-400',
      borderColor: 'border-emerald-500/30',
      bgGradient: 'from-emerald-500/20 to-teal-500/5',
      glow: 'shadow-emerald-500/20',
      stroke: '#10B981' // emerald-500
    };
    if (s <= 65) return {
      label: 'Mixed Signals',
      color: 'text-amber-400',
      borderColor: 'border-amber-500/30',
      bgGradient: 'from-amber-500/20 to-orange-500/5',
      glow: 'shadow-amber-500/20',
      stroke: '#F59E0B' // amber-500
    };
    return {
      label: 'Concerning',
      color: 'text-red-400',
      borderColor: 'border-red-500/30',
      bgGradient: 'from-red-500/20 to-rose-500/5',
      glow: 'shadow-red-500/20',
      stroke: '#DC2626' // red-600
    };
  };
  const category = getScoreCategory(score);
  const scoreDiff = previousScore ? score - previousScore : 0;
  const isImprovement = scoreDiff < 0;
  return <div className="relative min-h-[85vh] flex flex-col items-center justify-center text-center px-4 py-16 overflow-hidden" data-id="element-1361" data-mp="pevb1qsiyq13">
      {/* Ambient Background Effects */}
      <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-1362" data-mp="3syhkg9iq4jym">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[100px] animate-pulse" data-id="element-1363" data-mp="1rn5r8v3jgz2" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-red-600/10 rounded-full blur-[120px] animate-pulse delay-1000" data-id="element-1364" data-mp="6j55xupapdiz" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay" data-id="element-1365" data-mp="35611lwr5wny9"></div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }} className="relative z-10 flex flex-col items-center w-full max-w-4xl mx-auto" data-id="element-1366" data-mp="hb5bl6du6lfc">
        {/* Header Badge */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2,
        duration: 0.5
      }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-8 shadow-lg shadow-black/20" data-id="element-1367" data-mp="3uigkvm01er6a">
          <Calendar size={14} className="text-purple-300" data-id="element-1368" data-mp="1irurukpvxyvs" />
          <span className="text-xs font-bold tracking-widest uppercase text-purple-200" data-id="element-1369" data-mp="1dve43rmllmj8">
            Monthly Report • {month} {year}
          </span>
        </motion.div>

        {/* Main Title */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-12 tracking-tight leading-tight" data-id="element-1370" data-mp="1qdgn3279ss36">
          Your Communication <br data-id="element-1371" data-mp="1cf9y6bkau2fn" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-white to-purple-200" data-id="element-1372" data-mp="1cfs4k10odx1a">
            Wrapped
          </span>
        </h1>

        {/* Score Visualization */}
        <div className="relative w-72 h-72 md:w-80 md:h-80 mx-auto mb-12 group" data-id="element-1373" data-mp="254gyng3bd23j">
          {/* Glow Effect */}
          <div className={`absolute inset-0 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-700 ${category.color.replace('text-', 'bg-')}`} data-id="element-1374" data-mp="1vcb6a595gint" />

          {/* SVG Ring */}
          <svg className="w-full h-full transform -rotate-90 drop-shadow-2xl" data-id="element-1375" data-mp="2o0p3iisezayb">
            {/* Track */}
            <circle cx="50%" cy="50%" r="45%" stroke="currentColor" strokeWidth="6" fill="transparent" className="text-white/5" data-id="element-1376" data-mp="2ssqp4vbh2e10" />
            {/* Progress */}
            <motion.circle cx="50%" cy="50%" r="45%" stroke={category.stroke} strokeWidth="6" fill="transparent" strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: score / 100
          }} transition={{
            duration: 2.5,
            ease: [0.16, 1, 0.3, 1],
            delay: 0.5
          }} className="drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]" data-id="element-1377" data-mp="v4xyjjh48pcf" />
          </svg>

          {/* Center Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1378" data-mp="26mfu3vojminv">
            <motion.div initial={{
            scale: 0.5,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 1,
            type: 'spring',
            stiffness: 100
          }} className="flex flex-col items-center" data-id="element-1379" data-mp="21sbmda7ge6lq">
              <span className="text-8xl md:text-9xl font-black text-white tracking-tighter leading-none" data-id="element-1380" data-mp="2nzp69k4uzaih">
                {Math.round(score)}
              </span>
              <span className={`text-sm font-bold tracking-widest uppercase mt-2 ${category.color}`} data-id="element-1381" data-mp="2x1f69ect3hrv">
                Toxicity Score
              </span>
            </motion.div>
          </div>
        </div>

        {/* Status & Insight */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.8,
        duration: 0.6
      }} className="flex flex-col items-center gap-4" data-id="element-1382" data-mp="3bs5xak6kqctx">
          <div className={`inline-flex items-center gap-2.5 px-6 py-3 rounded-2xl bg-gray-900/80 border ${category.borderColor} backdrop-blur-xl shadow-xl ${category.glow}`} data-id="element-1383" data-mp="1bqo6t8zyohkw">
            <Sparkles size={18} className={category.color} strokeWidth={2.5} data-id="element-1384" data-mp="1kp9q56d9c0mq" />
            <span className="text-white font-bold text-lg" data-id="element-1385" data-mp="1ze65lohn8fxd">
              Status: <span className={category.color} data-id="element-1386" data-mp="1ufuvrdrcm5f5">{category.label}</span>
            </span>
          </div>

          {previousScore !== undefined && <div className="flex items-center gap-2 text-sm font-medium text-gray-400 bg-black/20 px-4 py-2 rounded-full border border-white/5" data-id="element-1387" data-mp="28eccgriu6hnk">
              <TrendingUp size={16} className={isImprovement ? 'text-emerald-400 rotate-180' : 'text-red-400'} data-id="element-1388" data-mp="e1mmgkvl9slg" />
              <span data-id="element-1389" data-mp="3w1gsk9e0r09x">
                {scoreDiff === 0 ? 'No change from last month' : `${Math.abs(Math.round(scoreDiff))}% ${isImprovement ? 'improvement' : 'increase'} from last month`}
              </span>
            </div>}
        </motion.div>
      </motion.div>
    </div>;
}